import { JSX, useEffect } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { useTeamspaceWelcomeModal } from '@/features/teamspace';

import { useCognitoCheckUser } from '@/entities/user/currentUser';
import { setAuthForm, useViewerStore } from '@/entities/viewer';

import RaskLogo from '@/shared/assets/images/rask.svg';
import { PATHS } from '@/shared/constants/routes/paths';
import { IS_WHITE_LABEL } from '@/shared/environments';
import { AuthForm } from '@/shared/types/Cognito';
import { Fade } from '@/shared/ui/Fade';

import { InfoSection } from './components/InfoSection';
import { ChangePassword } from './forms/ChangePassword';
import { ConfirmEmail } from './forms/ConfirmEmail';
import { ForgotPassword } from './forms/ForgotPassword';
import { Signin } from './forms/Signin';
import { Signup } from './forms/Signup';
import { DATA_TEST_IDS } from './lib/constants';
import { SContainer, SContent, SFormsContainer, SLogo } from './styled';
import { setUTMTagsToLocalStorage } from './utils/setUTMTagsToLocalStorage';

setUTMTagsToLocalStorage();

const formComponent: Record<AuthForm, () => JSX.Element> = {
  [AuthForm.ChangePassword]: ChangePassword,
  [AuthForm.ConfirmEmail]: ConfirmEmail,
  [AuthForm.ForgotPassword]: ForgotPassword,
  [AuthForm.Signin]: Signin,
  [AuthForm.Signup]: Signup,
};

const SIGN_UP_TYPE = 'signup';

export const AuthPage = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const spaceId = searchParams.get('space');

  const authForm = useViewerStore((state) => state.authForm);
  const { setSpaceId } = useTeamspaceWelcomeModal();

  const renderAuthForm = () => {
    const FormComponent = formComponent[authForm];
    return <FormComponent />;
  };

  const { isPending: isUserAuthorizedChecking } = useCognitoCheckUser({
    onSuccess: () => navigate(PATHS.homepage),
  });

  useEffect(() => {
    if (typeParam === SIGN_UP_TYPE && IS_WHITE_LABEL) {
      setAuthForm(AuthForm.Signup);
    }
  }, [typeParam]);

  useEffect(() => {
    if (spaceId) {
      setSpaceId(spaceId);
    }
  }, [spaceId, setSpaceId]);

  if (isUserAuthorizedChecking) {
    return null;
  }

  return (
    <Fade>
      <SContainer>
        <SContent $withoutPadding={IS_WHITE_LABEL}>
          {IS_WHITE_LABEL ? (
            <div />
          ) : (
            <SLogo alt="Rask" data-testid={DATA_TEST_IDS.logoRask} height="24" src={RaskLogo} width="140" />
          )}
          <SFormsContainer>{renderAuthForm()}</SFormsContainer>
        </SContent>

        <InfoSection />
      </SContainer>
    </Fade>
  );
};

export default AuthPage;
